import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Patients.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { PlainButton } from "@medoma/components/Button/Button";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import {
  getPatientNameWithStatus,
  sitesQueryOptions,
  usePatients,
} from "@/api/Patients";
import {
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import NoResults from "@/components/NoResults/NoResults";
import { ActionCardLink } from "@/components/ActionCard/ActionCard";
import { useQuery } from "@tanstack/react-query";
import { Text } from "@medoma/components/Text/Text";
import { useSearchParams } from "react-router";
import { useMemo } from "react";
import { SearchPanel } from "./SearchPanel";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";
import { ControlledSelect } from "@medoma/components/Select/Select";
import { useAuth } from "react-oidc-context";
import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";

const ALL_SITES = "";

export const Patients = () => {
  const { t } = useLingui();
  const { user } = useAuth();
  if (!user) {
    throw new Error("No user found");
  }
  const userId = user.profile.sub;

  const [searchParams] = useSearchParams();
  const currentFilter = searchParams.get("filter");
  const searchQuery = currentFilter ?? "";

  const {
    data: sites,
    isPending: isSitesPending,
    isError: isSitesError,
    error: sitesError,
  } = useQuery(sitesQueryOptions);

  const [selectedSite, setSelectedSite] = useLocalStorage({
    key: "patients.selectedSite",
    userId,
    initialValue: ALL_SITES,
  });

  const {
    data: patients,
    isPending,
    isError,
    error,
  } = usePatients({
    statuses: [
      patientStatusSchema.enum.admitted,
      patientStatusSchema.enum.prospect,
    ],
    sort: "name-asc",
  });

  const filteredPatients = useMemo(() => {
    if (!patients) {
      return [];
    }

    const siteFilteredPatients = selectedSite
      ? patients.filter((patient) => patient.site.id === selectedSite)
      : patients;

    const nameFilteredPatients = searchQuery
      ? siteFilteredPatients.filter((patient) =>
          filterByNameTerms(getPatientFullName(patient.name), searchQuery),
        )
      : siteFilteredPatients;

    return nameFilteredPatients;
  }, [patients, searchQuery, selectedSite]);

  if (isPending || isSitesPending) {
    return <Loading message={t`Hämtar patienter`} />;
  }

  if (isSitesError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta enheter.`} ${deducedError(sitesError)}`}
      />
    );
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienter.`} ${deducedError(error)}`}
      />
    );
  }

  const admittedPatients = filteredPatients.filter(
    (patient) => patient.status === patientStatusSchema.enum.admitted,
  );

  const prospectsPatients = filteredPatients.filter(
    (patient) => patient.status === patientStatusSchema.enum.prospect,
  );

  const selectedSiteDisplayName = sites?.find(
    ({ id }) => id === selectedSite,
  )?.displayName;
  const selectedSiteLabel = selectedSiteDisplayName
    ? t`${selectedSiteDisplayName}-enheten`
    : t`vald enhet`;

  const displaySiteNameOnPatientItem =
    sites && sites.length > 1 && selectedSite === ALL_SITES;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading level="h1">
          <Trans>Patienter</Trans>
        </Heading>
        <PlainButton asLinkTo="register" contentType="text-and-icon">
          <PlusIcon />
          <Trans>Registrera patient</Trans>
        </PlainButton>
      </div>

      {sites && sites.length > 1 ? (
        <ControlledSelect
          label={t`Enhet`}
          hideLabel
          value={selectedSite}
          onChange={setSelectedSite}
        >
          <option value={ALL_SITES}>{t`Alla enheter`}</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.displayName}
            </option>
          ))}
        </ControlledSelect>
      ) : null}

      <SearchPanel />

      <Heading level="h2" id="admitted-patients-list-label">
        <Trans>Inskrivna</Trans>
      </Heading>

      {admittedPatients.length > 0 ? (
        <ul
          className={styles.list}
          aria-labelledby="admitted-patients-list-label"
        >
          {admittedPatients.map((patient) => (
            <li key={patient.id}>
              <ActionCardLink
                to={`/patients/${patient.id}`}
                title={getPatientNameWithStatus(patient, "full")}
              >
                {displaySiteNameOnPatientItem ? (
                  <Text element="span" color="faded">
                    {patient.site.displayName}
                  </Text>
                ) : null}
              </ActionCardLink>
            </li>
          ))}
        </ul>
      ) : (
        <NoResults
          message={
            selectedSite
              ? t`Inga inskrivna patienter på ${selectedSiteLabel}`
              : t`Inga inskrivna patienter`
          }
        />
      )}

      <Heading level="h2" id="prospects-patients-list-label">
        <Trans>Under bedömning</Trans>
      </Heading>

      {prospectsPatients.length > 0 ? (
        <ul
          className={styles.list}
          aria-labelledby="prospects-patients-list-label"
        >
          {prospectsPatients.map((patient) => (
            <li key={patient.id}>
              <ActionCardLink
                to={`/patients/${patient.id}`}
                title={getPatientFullName(patient.name)}
              >
                {displaySiteNameOnPatientItem ? (
                  <Text element="span" color="faded">
                    {patient.site.displayName}
                  </Text>
                ) : null}
              </ActionCardLink>
            </li>
          ))}
        </ul>
      ) : (
        <NoResults
          message={
            selectedSite
              ? t`Inga patienter under bedömning på ${selectedSiteLabel}`
              : t`Inga patienter under bedömning`
          }
        />
      )}
    </div>
  );
};
